/**
 * Golem fork (FT-1180): THE THINGS YOU CAN DO TO A SEAT — the list itself,
 * once, for every surface that offers it.
 *
 * WHY THIS FILE EXISTS. FT-1169 built ONE component and opened it from two
 * gestures. The user's verdict was that this made both of them bad ("you
 * reused the elements for both the nameplate click and the hover coin which
 * made them both bad"), and they are right: the two schemes want opposite
 * shapes — a ring of little coins hung on the player's own rim, and a glassy
 * plate laid ON the coin. What the two actually share is not a box, it is a
 * VOCABULARY. So the vocabulary moves here and each scheme draws it its own
 * way.
 *
 * A `variant` prop on one component would have been the same mistake wearing
 * a flag, so there isn't one: `SeatRing.vue` and `SeatMenu.vue` know nothing
 * about each other, and neither of them knows what an action MEANS. They are
 * handed a list and they draw it.
 *
 * ── EVERY ACTION, EVERY TIME ────────────────────────────────────────────────
 * The user's last line was the important one: "also neither of them have all
 * of the needed buttons?" FT-1169 made a row's guard decide whether the row
 * EXISTED, so an open chair offered three entries and a dealt one six, and
 * nothing on screen ever admitted that the other three were a thing this app
 * can do. A menu that changes shape every time teaches nobody what it can do.
 *
 * So the list is FIXED. `guard` no longer decides absence — it decides
 * DISABLED, and it returns the REASON as a string, which the surface shows on
 * hover. A disabled row with a reason teaches on the first look; a missing
 * row teaches nothing, ever.
 *
 * FT-1260 AMENDS THE FIXED-LIST RULE FOR THE OWNER'S OWN MENUS: the Control
 * settings tab now lets a storyteller turn individual actions off (and
 * reorder them) per menu, so a menu MAY be missing rows — but only rows its
 * own owner chose to hide. The teaching duty this rule protects moves to the
 * settings list itself, which always shows EVERY action (seatActionSlots
 * below renders from this file, so a future entry appears there
 * automatically). Guards are untouched: a visible row still says why it is
 * refused rather than vanishing.
 *
 * ── WHAT IS KEPT FROM FT-1169, BECAUSE IT WAS RIGHT ─────────────────────────
 * Every entry carries the SAME condition as the direct mark it duplicates —
 * `kill` is the shroud's own toggle, `role` the coin's own set-role, the two
 * moves are the drags' own destinations, `nominate` the accusing mark's own
 * call, `ghost-vote` the cowl's own toggle. Two answers to "may this happen
 * now" is how a menu and a mark drift apart. The only thing that changed is
 * what a failing condition COSTS the row.
 *
 * ── THE ONE SLOT THAT HOLDS TWO ACTIONS ─────────────────────────────────────
 * `nominate` and `ghost-vote` share position five, and that is the user's own
 * rider from FT-1169 rather than a saving: they are the same question at two
 * moments — a living seat can point at somebody, a dead one can only spend
 * the vote it has left — and the coin already shares one corner between the
 * accusing hand and the ghost-vote cowl for exactly that reason. Which of the
 * two is present is decided by `slot`, not by a guard, so the list is six
 * long on every seat in the town.
 */

/**
 * ── FT-1194: THE APP'S OWN ART ON THE ROWS ─────────────────────────────────
 * Four entries now carry an `img` — a baked painted mark — instead of a Font
 * Awesome glyph, because the app already SAYS these things in its own art and
 * an icon teaches fastest by recognition:
 *
 *   role         ui-role.png — the toothed coin, the exact mark HostTools'
 *                "Roles" row already wears for "a character on a coin"
 *   move-player  ui-move-player.png — ui-seat's own chair + a bold arrow,
 *                baked to the row-mark family's recipe (this PERSON changes
 *                chairs)
 *   move-role    ui-move-role.png — ui-role's own toothed coin + the same
 *                arrow (this CHARACTER changes owners). Plain coin = change
 *                role; coin with the arrow = the coin goes elsewhere.
 *   nominate     ui-nominate-hand.png — the accusing hand the coin's own
 *                nominate mark wears (FT-1069d)
 *
 * The arrow is ONE bold arrow, not an opposed pair — both were baked and
 * judged at 18px (the plate row's icon box), where the pair's second head
 * dissolves into the shaft under it. The exchange nuance stays in the label
 * and hint; the icon's job is "this thing goes elsewhere".
 *
 * `img` is declared HERE, once, exactly like `icon` — both surfaces (the
 * plate's rows, the ring's little coins) render whichever the entry carries,
 * so changing an entry's art changes both schemes in one line. The old
 * `icon` names stay on the entries as the stood-down record (and they remain
 * registered in main.js); a resolved entry with an `img` is drawn from the
 * img and the icon is ignored.
 *
 * The imports are webpack asset resolutions, same as every component's — this
 * file stays free of stores, components and the DOM.
 */
import uiRole from "../assets/ui-role.png";
import uiMovePlayer from "../assets/ui-move-player.png";
import uiMoveRole from "../assets/ui-move-role.png";
import uiNominateHand from "../assets/ui-nominate-hand.png";

/**
 * ── FT-1212: TWO MORE ROWS LEAVE THE STOCK SET ─────────────────────────────
 *
 *   kill      ui-dead.png / ui-alive.png — the app's OWN death and life
 *             marks, the exact pair TownInfo's counts already wear
 *             (golem/glyphs' COUNT_ICONS.dead/.alive, the bone-tone chrome
 *             family). faSkull was upstream's word for death; this app
 *             already says it in its own paint, at 17px, every game. The
 *             row keeps its per-state swap: the skull kills, the heart
 *             revives — same two meanings the count strip gives them.
 *   reminder  ui-note.png — a note sheet, dog-eared corner, writing lines
 *             knocked out to transparency, baked NEW to the row-mark recipe
 *             (claude_temp_test/2026-08-26-ft1212-note-bake.mjs) because the
 *             app had no note mark to reuse: the custom reminder's icon is
 *             question marks and the add disc wore upstream's stock plus. A
 *             plus says "more"; what the row puts on a seat is a NOTE, and
 *             the nameplate-hover add disc (Player.vue) now wears this same
 *             art so the two surfaces that mean "put a note on this seat"
 *             say it with one mark.
 */
import uiDead from "../assets/ui-dead.png";
import uiAlive from "../assets/ui-alive.png";
import uiNote from "../assets/ui-note.png";
// FT-1242: the ghost-vote row wears the seat's OWN ghost-vote art — the same
// pale raised hand the coin's unspent-vote pip is drawn from — instead of the
// stock ballot glyph. One meaning, one mark: the row toggles exactly the
// thing that hand depicts.
// FT-1248 (user): "the use ghost vote in the player name click menu should
// be the same one as next to the coin" — and the coin's mark IS the cowl
// (FT-996 made ui-ghost-cowl the one meaning of an unspent ghost vote; the
// old hand, ui-ghost-vote-cowl, is a retiree). The row now wears the cowl.
import uiGhostVote from "../assets/ui-ghost-cowl.png";
// FT-1332: the execution row wears the seat grammar's own noose — the same
// art the marked seat, the chronicle strip and the vote overlay's toggle
// already use for "to be executed". One meaning, one mark.
import uiNoose from "../assets/ui-noose.png";

/**
 * The seat facts every guard reads. Player.vue builds one of these
 * (`seatActionContext`) and hands it in; nothing here touches a store, a
 * component or the DOM, which is what makes the list testable from a probe
 * and readable in one screen.
 *
 * @typedef {Object} SeatFacts
 * @property {boolean} isDead        the shroud is on
 * @property {boolean} isVoteless    a ghost that has spent its one vote
 * @property {boolean} hasPlayer     somebody has claimed this chair
 * @property {boolean} hasRole       a character is sitting on it
 * @property {boolean} roleArmed     that character is currently in hand
 * @property {boolean} lockedVote    a vote is running and locked
 * @property {boolean} nomination    a nomination is already up
 * (FT-1294: `grimoireHidden` stood here, carrying the face-down flag in for
 *  the reminder row's guard. The face-down state is retired — see
 *  store/index.js — so the fact has nothing left to report and the seat no
 *  longer computes it.)
 * @property {?string} whisperRefusal FT-1206: why this seat cannot be
 *                                    whispered right now, or null when it can.
 *                                    COMPUTED BY THE SEAT (Player.vue, from
 *                                    golem/chat's whisperRefusal — the chat
 *                                    level, the viewer, the ring) because the
 *                                    rule is the chat's, not the seat's; this
 *                                    file only carries the answer, exactly as
 *                                    every other guard carries a store fact.
 * @property {boolean} isOwnSeat     FT-1271: this chair is the VIEWER'S OWN —
 *                                   Player.vue's `isOwnSeat`, the same
 *                                   three-part identity test the "you" class
 *                                   and the coin drag already run.
 * @property {boolean} isMarked      FT-1332: THIS seat carries the execution
 *                                   mark (session.markedPlayer is this seat's
 *                                   index — the same state the nomination
 *                                   overlay's noose toggle drives).
 * @property {boolean} isTraveler    FT-1332: the character on this coin is a
 *                                   traveler — exiled, never executed, the
 *                                   vote overlay's own rule (its isExile).
 * @property {boolean} isStoryteller FT-1271: the viewer is the storyteller
 *                                   (`!session.isSpectator`, the same sense
 *                                   golem/chat's `viewerOf` gives the word).
 *                                   The two facts arrive as a PAIR because
 *                                   every own-seat rule below is a rule about
 *                                   PLAYERS: the storyteller keeps every power
 *                                   on every seat, their own included.
 */

/**
 * FT-1271: MAY THIS VIEWER CHANGE THE CHARACTER ON THIS COIN — null, or the
 * reason not.
 *
 * A PLAYER MAY NOT REWRITE THEIR OWN COIN. The user's call, and the reason is
 * information rather than permission: their own coin carries the one fact the
 * storyteller gave them, and overwriting it destroys something they cannot get
 * back. Guessing is about OTHER seats — every surface that lets a player put a
 * character on a coin is a guess at somebody else's, and a guess at your own is
 * not a guess.
 *
 * The storyteller is never refused: theirs is the hand that deals, and a seated
 * storyteller's chair is still a chair they must be able to correct.
 *
 * EXPORTED, and that is the point of it being a function rather than three
 * lines inside the `role` entry's guard: the vocabulary's guard decides what
 * the MENUS draw (a disabled row carrying this exact sentence), while the
 * seat's own writer re-asks the same question before it emits — one expression,
 * so a row that says "refused" and a click that fires anyway can never both be
 * true. See Player.vue's `openRoleModal`.
 *
 * @param {SeatFacts} facts
 * @returns {?string}
 */
export function roleChangeRefusal(facts) {
  const f = facts || {};
  if (f.isStoryteller) return null;
  return f.isOwnSeat ? "This is your own character" : null;
}

/**
 * THE SIX, in the order they are drawn — the user's own order, from their
 * original spec: "Kill / Change role / Move player / Move role / Player
 * nominates / Add reminder".
 *
 * Each entry is:
 *   id      stable, and what the surface emits back
 *   slot    which of the six positions it occupies (two entries share five)
 *   only    an entry that is only present in one state of its slot
 *   icon    Font Awesome name, all six registered in main.js
 *   label   what the CLICK does, not what the state is — so "Revive" on a
 *           dead seat and "Put character back" on an armed one, exactly as
 *           FT-1169 wrote them
 *   hint    what the row does, for the surface's tooltip when it is ENABLED
 *   setLabel FT-1260: the SETTINGS-FACING name — state-neutral, because the
 *           Control tab's per-menu list names the action itself, not the
 *           seat-state face it happens to wear ("Kill / Revive", not
 *           whichever of the two this seat would show)
 *   guard   null when the act may happen, otherwise the reason it may not.
 *           That reason IS the disabled tooltip.
 *   armed   the act is currently running and clicking again undoes it
 *   act     the seat method to run. A NAME rather than a closure: the seat
 *           owns its own methods, and a string keeps this file free of any
 *           opinion about how a component is put together.
 */
const ENTRIES = [
  {
    id: "kill",
    slot: 1,
    // FT-1212: skull/heartbeat stood down for the app's own pair — see the
    // import note. The swap stays per-state, exactly as the glyphs' was.
    icon: (f) => (f.isDead ? "heartbeat" : "skull"),
    img: (f) => (f.isDead ? uiAlive : uiDead),
    label: (f) => (f.isDead ? "Revive" : "Kill"),
    setLabel: "Kill / Revive",
    hint: (f) =>
      f.isDead
        ? "Bring this player back to life"
        : "Kill this player — the shroud goes on",
    // Never refused. A storyteller may kill an open chair (the chair is what
    // dies in this app, not the person), and reviving is the same toggle.
    guard: () => null,
    act: "toggleStatus",
  },
  {
    id: "role",
    slot: 2,
    // FT-1194: the mask glyph stood down for the app's own coin mark — the
    // thing this row changes is what sits on the coin, and ui-role.png is
    // already that word elsewhere (HostTools' "Roles" row).
    icon: () => "mask",
    img: () => uiRole,
    label: () => "Change role",
    setLabel: "Change role",
    hint: () => "Pick the character sitting on this chair",
    // FT-1271: the one seat a PLAYER may not repaint is their own — see
    // roleChangeRefusal above for why, and for why the seat's writer asks the
    // same function rather than repeating the test.
    guard: (f) => roleChangeRefusal(f),
    act: "openRoleModal",
  },
  {
    id: "move-player",
    slot: 3,
    // FT-1194: redo-alt (a refresh arrow) stood down — it said "again", not
    // "elsewhere". The chair with the arrow is this PERSON changing chairs.
    icon: () => "redo-alt",
    img: () => uiMovePlayer,
    label: () => "Move player",
    setLabel: "Move player",
    hint: () => "Pick this player up — then pick the chair they move to",
    /**
     * TWO REASONS, and the ORDER matters: an open chair is the permanent
     * fact and a locked vote is the passing one, so the permanent one is
     * reported first. A storyteller reading "Not while a vote is locked" on
     * an empty chair would wait for the vote and find the row still dead.
     */
    guard: (f) => {
      if (!f.hasPlayer)
        return "This chair is open — there is no player to move";
      if (f.lockedVote) return "Not while a vote is locked";
      return null;
    },
    act: "movePlayer",
  },
  {
    id: "move-role",
    slot: 4,
    // FT-1194: people-arrows stood down — two PEOPLE trading is the other
    // row's meaning. The toothed coin with the arrow is this CHARACTER
    // changing owners; the plain coin one row up is changing what it is.
    icon: () => "people-arrows",
    img: () => uiMoveRole,
    label: (f) => (f.roleArmed ? "Put character back" : "Move role"),
    setLabel: "Move role",
    hint: () =>
      "Pick this chair's character up — then tap another seat to trade them over",
    guard: (f) =>
      f.hasRole ? null : "This chair has no character on it to move",
    armed: (f) => !!f.roleArmed,
    act: "armCharacter",
  },
  {
    id: "nominate",
    slot: 5,
    only: (f) => !f.isDead,
    // FT-1194: the stock pointing glyph stood down for the app's own hand —
    // the same accusing manicule the coin's nominate mark wears (FT-1069d).
    icon: () => "hand-point-right",
    img: () => uiNominateHand,
    label: () => "Player nominates",
    setLabel: "Nominate",
    hint: () => "This player nominates — then pick who they point at",
    guard: (f) => (f.nomination ? "A nomination is already running" : null),
    act: "nominatePlayer",
  },
  {
    id: "ghost-vote",
    slot: 5,
    only: (f) => f.isDead,
    // FT-1242: vote-yea stood down for the ghost hand — see the import note.
    icon: () => "vote-yea",
    img: () => uiGhostVote,
    label: (f) => (f.isVoteless ? "Give ghost vote back" : "Use ghost vote"),
    setLabel: "Ghost vote",
    hint: (f) =>
      f.isVoteless
        ? "This ghost's vote is spent — hand it back"
        : "Spend this ghost's one vote",
    guard: () => null,
    act: "toggleGhostVote",
  },
  {
    id: "reminder",
    slot: 6,
    // FT-1212: the plus stood down for the note sheet — see the import note.
    icon: () => "plus",
    img: () => uiNote,
    label: () => "Add reminder",
    setLabel: "Add reminder",
    hint: () => "Put a reminder token on this seat",
    /**
     * THE ROW THE USER FOUND MISSING, twice over. FT-1169 gated it on
     * `!grimoire.isPublic` and let the guard delete it, so with the coins
     * face down the seat's menu quietly lost an entry and never said why;
     * FT-1212 made it say so instead of vanishing.
     *
     * FT-1294: the gate is gone with the state behind it. Face-down is
     * retired (store/index.js), so the row was already unrefusable on every
     * client a person could actually be sitting at — a player's flag was
     * false, and the host had no way left to turn it true. Refusing on a
     * condition nothing can produce is a guard that only ever lies.
     */
    guard: () => null,
    act: "openReminderModal",
  },
  {
    /**
     * FT-1206: THE SEVENTH SLOT — whisper this seat. The same whisper the
     * Chronicle's composer sends (one send path, golem/chat's whisperFrame);
     * what this row opens is the inline input (SeatWhisper.vue), drawn by
     * each scheme in its own shape. The guard's reasons are the chat level's
     * own words ("Chat is off", "Whispers are off", "Only your neighbors"),
     * precomputed by the seat — see SeatFacts. The row keeps the fixed-list
     * rule: refused is drawn, never absent.
     */
    id: "whisper",
    slot: 7,
    // FT-1211: the plane stood down for the speech bubble — the user's own
    // reading: "the plane is a fun message SENT icon but we want this to be
    // clearly the icon to SEND a message". The plane keeps the places where
    // it means "sent" (the flying mark, the toast, the composer's send
    // button); the doorway that OPENS a message wears comment-dots — a
    // message being spoken, distinct from the Chat-level row's `comments`
    // (the chat feature itself). One change here reskins the plate row and
    // the ring's coin both — the point of the vocabulary.
    icon: () => "comment-dots",
    label: () => "Whisper",
    setLabel: "Whisper",
    hint: () => "Send this player a private message",
    guard: (f) => f.whisperRefusal || null,
    act: "openSeatWhisper",
  },
  {
    /**
     * FT-1332: THE EIGHTH SLOT — mark this seat for execution. The SAME
     * state the nomination overlay's noose toggle drives: one mutation,
     * `session/setMarkedPlayer` (FT-1311 made it seat-surgery-proof — it
     * broadcasts, writes the chronicle line and clears a standing tie on
     * its own), reached through the seat's `toggleExecutionMark`, never a
     * parallel path. The label follows the state the way Kill/Revive's
     * does — what the CLICK does, not what the state is — and `armed`
     * lights the row while THIS seat is the marked one, so the menu shows
     * where the mark stands. Marking a seat while another is marked simply
     * moves the mark: the mutation's own semantics.
     *
     * A traveler is exiled, not executed — the vote overlay hides its own
     * mark toggle for exactly this (its `isExile`) — so the row refuses
     * there with the reason on its tooltip, per the fixed-list rule.
     */
    id: "execution",
    slot: 8,
    // The FA name is the fallback only — the row always carries the noose
    // art below, and "skull" is already registered (the kill row's own).
    icon: () => "skull",
    img: () => uiNoose,
    label: (f) => (f.isMarked ? "Cancel execution" : "Mark for execution"),
    setLabel: "Mark for execution",
    hint: (f) =>
      f.isMarked
        ? "Call it off — clear this seat's execution mark"
        : "Mark this seat to be executed at the end of the day",
    guard: (f) => (f.isTraveler ? "A traveler is exiled, not executed" : null),
    armed: (f) => !!f.isMarked,
    act: "toggleExecutionMark",
  },
  {
    /**
     * FT-1454: TODAY'S NOMINATIONS, the storyteller's own hand on them. A
     * completed vote writes both badges by itself (session/addHistory); these
     * two rows are the on/off switch for each, which is also the reset when
     * the record is wrong either way. `armed` lights the row while the badge
     * stands, and the label stays the badge's own name so the row reads as a
     * switch rather than an order. Storyteller only: players never get the
     * menu (Player.vue's seatMenuEntries), and the guard says so regardless.
     */
    id: "nominated-today",
    slot: 9,
    icon: () => "hand-point-right",
    img: () => uiNominateHand,
    label: () => "Nominated today",
    setLabel: "Nominated today",
    hint: (f) =>
      f.hasNominatedToday
        ? "On — this player has nominated today. Click to clear it"
        : "Off — mark this player as having nominated today",
    guard: (f) =>
      f.isStoryteller ? null : "Only the storyteller keeps the day's record",
    armed: (f) => !!f.hasNominatedToday,
    act: "toggleNominatedToday",
  },
  {
    id: "was-nominated-today",
    slot: 10,
    icon: () => "skull",
    img: () => uiNoose,
    label: () => "Was nominated today",
    setLabel: "Was nominated today",
    hint: (f) =>
      f.wasNominatedToday
        ? "On — this player was nominated today. Click to clear it"
        : "Off — mark this player as nominated today",
    guard: (f) =>
      f.isStoryteller ? null : "Only the storyteller keeps the day's record",
    armed: (f) => !!f.wasNominatedToday,
    act: "toggleWasNominatedToday",
  },
];

/**
 * The six actions for one seat, resolved against its facts.
 *
 * @param {SeatFacts} facts
 * @returns {Array<{id, icon, label, title, disabled, reason, armed, act}>}
 */
export function seatActions(facts) {
  const f = facts || {};
  return ENTRIES.filter((e) => !e.only || e.only(f)).map((e) => {
    const reason = e.guard(f);
    const label = e.label(f);
    return {
      id: e.id,
      icon: e.icon(f),
      // FT-1194: the app's own art, when the entry carries any — the surface
      // draws `img` when it is non-empty and falls back to the FA `icon`
      // name, so one field here feeds both the plate and the ring.
      img: e.img ? e.img(f) : "",
      label,
      // ONE TOOLTIP FIELD, and which sentence is in it is the whole point of
      // this rework: enabled says what the row does, disabled says why it
      // cannot. The label rides along in the disabled case because a ring's
      // little coin carries no words of its own — that surface's only way of
      // naming an entry is this string.
      title: reason ? `${label} — ${reason}` : e.hint(f),
      disabled: !!reason,
      reason: reason || "",
      armed: e.armed ? e.armed(f) : false,
      act: e.act,
    };
  });
}

/** The whole vocabulary, in order, ignoring any seat — for a surface that
 *  wants to size itself to the widest label it will ever have to draw rather
 *  than to the one it happens to be showing. */
export function seatActionLabels() {
  return ENTRIES.map((e) =>
    e.label({ isDead: false, isVoteless: false, roleArmed: false }),
  );
}

// ---------------------------------------------------------------------------
// FT-1260: THE VOCABULARY AS ORDERABLE SLOTS — what the Control settings
// tab's per-menu lists render, and what the layout prefs are keyed by.
//
// The orderable unit is the SLOT, not the entry, because slot five holds two
// entries (nominate / ghost-vote) that are one question at two moments — the
// header's own note. Splitting them into two draggable lines would let a
// person order two things that can never both be on screen, so the pair is
// ONE list entry, labelled with both names, and its layout id is the first
// entry's ("nominate"). SEAT_SLOT_BY_ID maps every entry id back to its
// slot's layout id so a surface filtering resolved entries against a layout
// never has to know which entries share.
//
// Everything below derives from ENTRIES — an eighth action added above
// appears in the settings list, the prefs' sanitizer and both menus with no
// second list to update.
// ---------------------------------------------------------------------------

/** The state-neutral face an icon/img resolver is asked with — the same
 *  facts seatActionLabels uses. */
const NEUTRAL_FACTS = { isDead: false, isVoteless: false, roleArmed: false };

/** entry id → its slot's layout id (the slot's first entry's id). */
export const SEAT_SLOT_BY_ID = (() => {
  const firstBySlot = new Map();
  const map = {};
  ENTRIES.forEach((e) => {
    if (!firstBySlot.has(e.slot)) firstBySlot.set(e.slot, e.id);
    map[e.id] = firstBySlot.get(e.slot);
  });
  return map;
})();

/**
 * The vocabulary grouped by slot, in slot order — one row per orderable unit.
 *
 * @returns {Array<{id, ids, label, icon, img}>}
 *   id     the slot's layout id (its first entry's id)
 *   ids    every entry id the slot holds (["nominate", "ghost-vote"])
 *   label  the settings-facing name — shared slots join theirs with " / "
 *   icon   the first entry's FA name at the neutral face (fallback art)
 *   img    the first entry's baked mark at the neutral face, or ""
 */
export function seatActionSlots() {
  const slots = [];
  const bySlot = new Map();
  ENTRIES.forEach((e) => {
    let s = bySlot.get(e.slot);
    if (!s) {
      s = {
        id: e.id,
        ids: [],
        labels: [],
        icon: e.icon(NEUTRAL_FACTS),
        img: e.img ? e.img(NEUTRAL_FACTS) : "",
      };
      bySlot.set(e.slot, s);
      slots.push(s);
    }
    s.ids.push(e.id);
    s.labels.push(e.setLabel || e.label(NEUTRAL_FACTS));
  });
  return slots.map((s) => ({
    id: s.id,
    ids: s.ids,
    label: s.labels.join(" / "),
    icon: s.icon,
    img: s.img,
  }));
}
